import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AISettings, UpdateSettingFunction } from './types';
import { Zap, Target, MessageSquareText } from 'lucide-react';

/**
 * O que faz: Seção de Ativação da IA
 * Onde usa: AISettingsForm
 * 
 * Permite configurar quando e para quais leads a IA deve ser ativada
 * automaticamente, incluindo configurações de sugestões de chat
 * 
 * REFATORADO: Visual premium com ícones coloridos e melhor hierarquia
 */

interface AIActivationSectionProps {
  settings: AISettings;
  updateSetting: UpdateSettingFunction;
  readOnly?: boolean;
}

export const AIActivationSection = ({
  settings,
  updateSetting,
  readOnly = false
}: AIActivationSectionProps) => {
  return (
    <Card className="rounded-2xl border-border shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <Zap className="w-5 h-5 text-accent-foreground" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold text-foreground">
              Ativação da IA
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-0.5">
              Quando a IA deve ser ativada automaticamente
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-1">
        {/* Toggle: Todos os novos leads */}
        <div
          className={`flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${settings.ai_active_for_all_new_leads
              ? 'bg-accent border border-border'
              : 'bg-muted border border-transparent hover:bg-muted'
            }`}
        >
          <Label htmlFor="ai-all-leads" className="flex items-start gap-3 cursor-pointer flex-1">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${settings.ai_active_for_all_new_leads
                ? 'bg-accent'
                : 'bg-secondary'
              }`}>
              <Zap className={`w-4 h-4 ${settings.ai_active_for_all_new_leads
                  ? 'text-accent-foreground'
                  : 'text-muted-foreground'
                }`} />
            </div>
            <div className="space-y-0.5">
              <span className="font-medium text-foreground block">
                Ativar para todos os novos leads
              </span>
              <span className="text-sm text-muted-foreground block">
                A IA será ativada automaticamente para todos os novos leads
              </span>
            </div>
          </Label>
          <Switch
            id="ai-all-leads"
            checked={settings.ai_active_for_all_new_leads}
            onCheckedChange={checked => !readOnly && updateSetting('ai_active_for_all_new_leads', checked)}
            disabled={readOnly}
            className="ml-4"
          />
        </div>

        {/* Toggle: Apenas leads de anúncios */}
        <div
          className={`flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${settings.ai_active_for_ad_leads_only
              ? 'bg-info-subtle border border-info/30'
              : 'bg-muted border border-transparent hover:bg-muted'
            }`}
        >
          <Label htmlFor="ai-ad-leads" className="flex items-start gap-3 cursor-pointer flex-1">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${settings.ai_active_for_ad_leads_only
                ? 'bg-info-subtle'
                : 'bg-secondary'
              }`}>
              <Target className={`w-4 h-4 ${settings.ai_active_for_ad_leads_only
                  ? 'text-info-text'
                  : 'text-muted-foreground'
                }`} />
            </div>
            <div className="space-y-0.5">
              <span className="font-medium text-foreground block">
                Apenas leads de anúncios
              </span>
              <span className="text-sm text-muted-foreground block">
                Ativa apenas para leads de campanhas pagas
              </span>
            </div>
          </Label>
          <Switch
            id="ai-ad-leads"
            checked={settings.ai_active_for_ad_leads_only}
            onCheckedChange={checked => !readOnly && updateSetting('ai_active_for_ad_leads_only', checked)}
            disabled={readOnly}
            className="ml-4"
          />
        </div>

        {/* Toggle: Sugestões de chat */}
        <div
          className={`flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${settings.ai_chat_suggestions_active
              ? 'bg-accent border border-border'
              : 'bg-muted border border-transparent hover:bg-muted'
            }`}
        >
          <Label htmlFor="ai-suggestions" className="flex items-start gap-3 cursor-pointer flex-1">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${settings.ai_chat_suggestions_active
                ? 'bg-accent'
                : 'bg-secondary'
              }`}>
              <MessageSquareText className={`w-4 h-4 ${settings.ai_chat_suggestions_active
                  ? 'text-accent-foreground'
                  : 'text-muted-foreground'
                }`} />
            </div>
            <div className="space-y-0.5">
              <span className="font-medium text-foreground block">
                Sugestões de chat
              </span>
              <span className="text-sm text-muted-foreground block">
                A IA sugere respostas durante conversas
              </span>
            </div>
          </Label>
          <Switch
            id="ai-suggestions"
            checked={settings.ai_chat_suggestions_active}
            onCheckedChange={checked => !readOnly && updateSetting('ai_chat_suggestions_active', checked)}
            disabled={readOnly}
            className="ml-4"
          />
        </div>
      </CardContent>
    </Card>
  );
};